import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { NetworkPartner, Role } from '@kion/data-models';
import { RolesFacade } from '@kion/data-access/data-access-roles';
import { NetworkPartnersFacade } from '@kion/data-access/data-access-network-partners';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '@kion/ui';
import { map, take } from 'rxjs/operators';

interface UserRoles {
  userId: string,
  baseSite: string
}

@Component({
  selector: 'kion-role-map-dialog',
  templateUrl: './role-map-dialog.component.html',
  styleUrls: ['./role-map-dialog.component.scss']
})
export class RoleMapDialogComponent implements OnInit {

  roles$: Observable<Role[]>;
  networkPartners$: Observable<NetworkPartner[]>;
  basestores$: Observable<{key: string, value: string, selected?: boolean}[]>;
  form: FormGroup;

  data: UserRoles;

  roles = [
    {
      id: 'fsNwpAdmin',
      name: 'NetworkPartner Admin',
      isSelected: false
    },
    {
      id: 'fsNwpViewer',
      name: 'NetworkPartner Viewer',
      isSelected: false
    },
    {
      id: 'fsNwpBuyer',
      name: 'NetworkPartner Buyer',
      isSelected: false
    },
    {
      id: 'fsNwpApprover',
      name: 'NetworkPartner Approver',
      isSelected: false
    }
  ];

  constructor(
    private rolesFacade: RolesFacade,
    private networkPartnersFacade: NetworkPartnersFacade,
    formBuilder: FormBuilder,
    private correlationObserver: CorrelationObserverService,
    private toastMessageService: ToastService,
    private translationService: TranslateService,
    private dialogRef: NgbActiveModal
  ) {
    this.form = formBuilder.group({
      baseStore: new FormControl()
    });
    this.networkPartners$ = this.networkPartnersFacade.all$;
    this.roles$ = this.rolesFacade.all$.pipe(
      map(
        (roles: Role[]) => roles.filter(role => role.userId === this.data.userId)
      )
    );
    this.form.get('baseStore').valueChanges.subscribe(
      (baseStore) => {
        this.roles$.pipe(take(1)).subscribe(
          (roles: Role[]) => {
            this.roles = this.roles.map(
              (role) => {
                return {
                  ...role,
                  isSelected: !!roles.find(r => r.role.uid === role.id && baseStore === r.baseStore)
                }
              }
            )
          }
        )
      }
    );
  }

  ngOnInit(): void {
    this.rolesFacade.loadByUserId(this.data.userId);
    this.basestores$ = this.networkPartners$.pipe(map((networkPartners) => {
          return networkPartners.map((networkpartner) => {
            return { value: networkpartner.name.concat(' -- ',networkpartner.id), key: networkpartner.id, selected: false}
          })
        }),
    )
  }

  close(): void {
    this.dialogRef.close();
  }

  setRole(role: string): void {
    const request = {
      baseStore: this.form.get('baseStore').value,
      role: {
        uid: role
      }
    };
    this.correlationObserver.subscribeToAction(
      this.rolesFacade.setRoles(this.data.userId, [request]),
      () => this.rolesFacade.loadByUserId(this.data.userId),
      () =>  this.toastMessageService.show(
        {
          text: this.translationService.instant('admin.toast.role.update.error'),
          type: 'error',
        }
      )
    )
  }

  revokeRole(role: string): void {
    const request = {
      id: this.data.userId + this.form.get('baseStore').value + role,
      baseStore: this.form.get('baseStore').value,
      role: {
        uid: role
      }
    };
    this.correlationObserver.subscribeToAction(
      this.rolesFacade.revokeRoles(this.data.userId, [request]),
      () => this.rolesFacade.loadByUserId(this.data.userId),
      () => this.toastMessageService.show(
        {
          text: this.translationService.instant('admin.toast.role.update.error'),
          type: 'error',
        })
    )
  }
}
